import { isBefore } from 'date-fns'
import { useLocale } from 'next-intl'
import { useRef, KeyboardEvent, ChangeEvent } from 'react'

import { StringDateRange, useFormatDate } from '@/shared/lib'
import { type RangeDatePickerRef } from '@/shared/ui'

import { useDateRange } from '../model/date-range'
import { useKeyword } from '../model/keyword'
import {
  convertToSearchHistory,
  type KeywordSearchHistory,
} from '../model/keyword-search-history-store/history'
import { useKeywordSearchHistoryStore } from '../model/keyword-search-history-store/store'

type Props = {
  search: (params: {
    keyword: string
    startDate?: string
    endDate?: string
  }) => void
  close: () => void
}

export const useMobileKeywordSearch = ({ search, close }: Props) => {
  const locale = useLocale()
  const { formatDateRange } = useFormatDate()

  const periodPickerRef = useRef<RangeDatePickerRef>({
    focus: () => {},
  })

  const historyActions = useKeywordSearchHistoryStore(state => state.actions)

  const { dateRange, updateDateRange } = useDateRange()
  const { keyword, updateKeyword } = useKeyword()

  const moveFocusToPeriodPicker = () => {
    periodPickerRef.current.focus()
  }

  const handleDateRangeChange = (newDateRange?: StringDateRange) => {
    const trimmedKeyword = keyword.trim()

    historyActions.addSearchHistory(
      convertToSearchHistory({
        keyword: trimmedKeyword,
        dateRange: newDateRange,
        locale,
      }),
    )

    search({
      keyword: trimmedKeyword,
      startDate: newDateRange?.from,
      endDate: newDateRange?.to,
    })

    updateDateRange(newDateRange)

    close()
  }

  const handleDateRangeReset = () => {
    updateDateRange(undefined)
  }

  const handleRecommendKeywordClick = (keyword: string) => {
    historyActions.addSearchHistory(
      convertToSearchHistory({ keyword, dateRange, locale }),
    )

    search({
      keyword,
      startDate: dateRange?.from,
      endDate: dateRange?.to,
    })

    updateKeyword(keyword)

    close()
  }

  const handleRecentHistoryClick = (history: KeywordSearchHistory) => {
    const { keyword, startDate, endDate } = history

    const today = new Date().setHours(0, 0, 0, 0)
    const isBeforeToday = isBefore(startDate, today)

    if (isBeforeToday) {
      updateKeyword(keyword)
      updateDateRange(undefined)

      // TODO: 날짜 바텀시트 띄우기
      moveFocusToPeriodPicker()

      return
    }

    historyActions.addSearchHistory(history)

    search({
      keyword,
      startDate,
      endDate,
    })

    updateKeyword(keyword)
    updateDateRange({
      from: startDate,
      to: endDate,
    })

    close()
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateKeyword(event.target.value)
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSubmit()
    }
  }

  const handleInputReset = () => {
    updateKeyword('')
  }

  const handleSubmit = () => {
    const trimmedKeyword = keyword.trim()

    if (trimmedKeyword || dateRange) {
      historyActions.addSearchHistory(
        convertToSearchHistory({ keyword: trimmedKeyword, dateRange, locale }),
      )
    }

    search({
      keyword: trimmedKeyword,
      startDate: dateRange?.from,
      endDate: dateRange?.to,
    })

    close()
  }

  return {
    keywordInputProps: {
      value: keyword,
      onChange: handleInputChange,
      onKeyDown: handleInputKeyDown,
      onReset: handleInputReset,
    },
    onRecentHistoryClick: handleRecentHistoryClick,
    onRecommendKeywordClick: handleRecommendKeywordClick,
    dateRange: formatDateRange({ dateRange }),
    handleDateRangeChange,
    handleDateRangeReset,
  }
}
